import axios from "axios";
import { getApiUrl } from "@utils/baseUrl";

export type CompanyProfile = { slug: string; companyId: string };

// Profiles already looked up, by master company id, kept for the browser session so the same
// company is never asked for twice. A company with no profile is not stored, so it is retried.
const STORAGE_KEY = "hermes:company-profiles";

function restore(): [string, CompanyProfile][] {
  try {
    return Object.entries(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"));
  } catch {
    return [];
  }
}

const cache = new Map<string, CompanyProfile>(restore());
const inFlight = new Map<string, Promise<CompanyProfile | null>>();

export const cachedProfile = (masterId: string): CompanyProfile | undefined => cache.get(masterId);

/** The profile page of a company, carrying the country and type of the page Hermes was opened from. */
export function profileUrl(profile: CompanyProfile, linkQuery: string): string {
  const base = `/companies/${profile.slug}/${profile.companyId}`;
  return linkQuery ? `${base}?${linkQuery}` : base;
}

/**
 * The profile of one company (GET companyProfiles/getSlug, the lookup the rest of the app uses),
 * asked for when its name is clicked rather than for every row of a page. Null when it has none.
 */
export function resolveCompanyProfile(masterId: string): Promise<CompanyProfile | null> {
  const hit = cache.get(masterId);
  if (hit) return Promise.resolve(hit);
  const pending = inFlight.get(masterId);
  if (pending) return pending;

  const request = axios
    .get(getApiUrl("companyProfiles/getSlug"), { params: { companyId: masterId } })
    .then(({ data }) => {
      const found = data?.data;
      if (String(data?.status).toUpperCase() !== "SUCCESS" || !found?.companySlug || !found?.aliasCompanyId) return null;
      const profile: CompanyProfile = { slug: found.companySlug, companyId: String(found.aliasCompanyId) };
      cache.set(masterId, profile);
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(cache)));
      } catch {
        // Storage can be unavailable; the in-memory copy still serves this session.
      }
      return profile;
    })
    .finally(() => inFlight.delete(masterId));
  inFlight.set(masterId, request);
  return request;
}
